import INTOUCH_API from "../../services/Api";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useCookie from "../../hooks/useCookie";
import useCustomNavigate from "../../hooks/useCustomNavigate";
import useAuth from "../../hooks/useAuth";
import PersonalAccount from "../../store/PersonalAccount";
import useValidateForm from "../../hooks/useValidateForm";
import useBrowserStorage from "../../hooks/useBrowserStorage";
import { eighteenYearAgo } from "../../utils/helpers";
import {
  CURRENT_TEMP_DEMO_GENDER,
  TEMPORARY_ID_KEY,
  LOYALTY_REF_CODE_KEY,
} from "../../utils/constants";
import ROUTES from "../../utils/routes.js";
import { MODAL_TYPES } from "../../utils/emitter";
import useModalActions from "../../hooks/useModalActions";
import { useTranslation } from "react-i18next";
import useCurrentGame from "../../hooks/useCurrentGame";
import useEmailCodeConfirm from "../../hooks/useEmailCodeConfirm";

export default function useController() {
  const {
    getValidGenders,
    onComparableTextHandle,
    checkConsistency,
    validateBirthday,
    isValidEmail,
    isValidPassword,
    isConsistency,
    comparableText,
    isBirthdayValid,
    formatDate,
    validateTextInput,
    isValidTextInput,
    isValidGenders,
    manageValidate,
    setComparableIsEmpty,
    comparableIsEmpty,
    setIsValidEmail,
    setIsValidPassword,
    setIsConsistency,
  } = useValidateForm();
  const [isTermsAccept, setIsTermsAccept] = useState(false);
  const [isTermsAcceptValid, setIsTermsAcceptValid] = useState(true);
  const [firstAcceptClick, setFirstAcceptClick] = useState(true);
  const [isPrivacyAccept, setIsPrivacyAccept] = useState(false);
  const [isPrivacyAcceptValid, setIsPrivacyAcceptValid] = useState(true);
  const [firstPrivacyClick, setFirstPrivacyClick] = useState(true);
  const onCheckTerms = useCallback(() => {
    if (firstAcceptClick) {
      setIsTermsAcceptValid(!isTermsAccept);
      setFirstAcceptClick(false);
    }
    setIsTermsAccept(!isTermsAccept);
  }, [isTermsAccept, firstAcceptClick]);
  const onCheckPrivacy = useCallback(() => {
    if (firstPrivacyClick) {
      setIsPrivacyAcceptValid(!isPrivacyAccept);
      setFirstPrivacyClick(false);
    }
    setIsPrivacyAccept(!isPrivacyAccept);
  }, [isPrivacyAccept, firstPrivacyClick]);
  const [isRegistrationLoading, setIsRegistrationLoading] = useState(false);
  const [responseServerErr, setResponseServerErr] = useState("");
  const navigate = useNavigate();
  const { getCookie, removeCookie } = useCookie();
  const { navigateTo } = useCustomNavigate();
  const { getCurrentGame } = useCurrentGame();
  const { confirmEmail } = useEmailCodeConfirm();
  const { setToken } = useAuth();
  const { getItem, removeItem } = useBrowserStorage();
  const location = useLocation();
  const { openModal } = useModalActions();
  const { t } = useTranslation();

  const [registerFormData, setRegisterFormData] = useState({
    firstName: "",
    birthday: formatDate(eighteenYearAgo),
    email: "",
    password: "",
    gender: "",
    buyLater: 0,
  });

  const handleComparableTextHandle = (event) => {
    onComparableTextHandle(event);
    setIsConsistency(true);
    setComparableIsEmpty(false);
  };

  const handleBlur = useCallback((e) => {
    const name = e.target.name;
    const value = e.target.value;
    if (name === "email" || name === "password") {
      manageValidate(name, value);
    }
    setRegisterFormData((prevState) => {
      if (
        name === "repeat_pass" ||
        (name === "password" && !!prevState.repeat_pass)
      ) {
        checkConsistency(prevState.password, value);
      }
      return {
        ...prevState,
        [name]: value,
      };
    });
  }, []);

  const handleChange = useCallback((e) => {
    if (e.target.name === "gender") {
      // item 4: раньше блок держался ТОЛЬКО на куке inviterGender (жила 2 мин и успевала
      // протухнуть) — пол становился меняемым прямо на регистрации по инвайту. Теперь
      // блокируем при любом признаке инвайт-регистрации: пол назначает система по партнёру.
      const gender =
        getCookie("inviterGender") ||
        getCookie("isInvited") ||
        getCookie("invite_link");
      if (gender) {
        openModal(MODAL_TYPES.GENDER_NOTIFICATION, {
          type: "noauth",
          message: t("genderModal.noauthText"),
        });
        return;
      }
    }
    const name = e.target.name;
    const value = e.target.value;
    setRegisterFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (e.target.name === "firstName") validateTextInput(e.target.value);
    if (e.target.name === "email") setIsValidEmail(true);
    if (e.target.name === "password") setIsValidPassword(true);
  }, []);

  const onBirthdayHandle = useCallback(
    (value) => {
      setRegisterFormData((prevState) => ({
        ...prevState,
        birthday: formatDate(value),
      }));
      validateBirthday(formatDate(value));
    },
    [registerFormData.birthday]
  );
  const onSubmitForm = async (event) => {
    event.preventDefault();
    if (isRegistrationLoading) return;
    if (!isRegisterFormValid()) {
      getValidGenders(
        event.target.elements["g-man"].checked ||
          event.target.elements["g-woman"].checked
      );
      validateTextInput(event.target.elements.firstName.value);
      validateBirthday(event.target.elements.birthday.value);
      manageValidate("email", event.target.elements.email.value);
      manageValidate("password", event.target.elements.password.value);
      checkConsistency(event.target.elements.password.value, comparableText);
      setComparableIsEmpty(comparableText.length === 0);
      setIsTermsAcceptValid(isTermsAccept);
      setFirstAcceptClick(true);
      setIsPrivacyAcceptValid(isPrivacyAccept);
      setFirstPrivacyClick(true);
      return;
    }
    const temporaryUserId = getItem(TEMPORARY_ID_KEY);
    // копия: раньше дата переписывалась прямо в состоянии формы, а теперь форму
    // можно отправить ещё раз (окно с кодом закрыли и исправили почту)
    const formData = { ...registerFormData };
    formData.email = formData.email.trim().toLowerCase();
    formData.birthday = formData.birthday.split(".").reverse().join("-");
    if (temporaryUserId) {
      formData.temporaryUserId = temporaryUserId;
    }
    // 23.10: сначала код на почту, аккаунт создаётся после его ввода (EmailCodeModal)
    startLoadRegistration();
    const err = await confirmEmail(formData.email, (emailCode) =>
      registerUser({ ...formData, emailCode })
    );
    stopLoadRegistration();
    if (err) {
      setResponseServerErr(err);
      setTimeout(() => setResponseServerErr(""), 15000);
    }
  };

  const isRegisterFormValid = () => {
    return (
      !comparableIsEmpty &&
      isTermsAccept &&
      isPrivacyAccept &&
      isValidGenders &&
      isValidEmail &&
      isValidPassword &&
      isConsistency &&
      isBirthdayValid.validAge &&
      isBirthdayValid.validFormat &&
      isFormFilled()
    );
  };

  const isFormFilled = () => {
    return (
      registerFormData.firstName.length &&
      registerFormData.password.length &&
      registerFormData.email.length &&
      registerFormData.gender.length &&
      registerFormData.birthday.length
    );
  };

  const detectGender = useCallback(() => {
    const gender = getCookie("inviterGender");
    if (!gender) {
      const genderDemo = JSON.parse(
        localStorage.getItem(CURRENT_TEMP_DEMO_GENDER)
      );
      if (!genderDemo) return;
      setRegisterFormData({
        ...registerFormData,
        gender: genderDemo,
      });
    } else {
      setRegisterFormData({
        ...registerFormData,
        gender: gender === "male" ? "female" : "male",
      });
    }
  }, [registerFormData.gender]);

  // возвращает текст ошибки или null: ошибку показывает окно ввода кода
  const registerUser = async (formData) => {
    let errorMessage = null;
    const invite_link = getCookie("invite_link");
    // const errorLink = getCookie("errorLink");

    const ref = getItem("ref", "session");
    // per-user реф-код лояльности (ловится в App.js из ?ref=)
    const loyaltyRefCode = localStorage.getItem(LOYALTY_REF_CODE_KEY);
    if (loyaltyRefCode) {
      formData = { ...formData, refCode: loyaltyRefCode };
    }
    await INTOUCH_API.requestManager(
      "register",
      { formData, ref },
      async (response) => {
        localStorage.removeItem(LOYALTY_REF_CODE_KEY);
        // если есть temporaryUserId значит была начата тестовая игра с лендинга и посещен экран "Знакомство с собой"
        // тут записываем что посетили его, т.к логика
        const temporaryUserId = getItem(TEMPORARY_ID_KEY);
        if (temporaryUserId) {
          const game = getCurrentGame();
          if (game?.id) {
            await PersonalAccount.addGameIdsByMeets(
              game.id,
              response.data.userId
            );
          }
        }

        removeItem("ref", "session");
        removeCookie("inviterGender");

        setToken(response.data.token);
        await PersonalAccount.getPersonalAccountData();

        removeCookie("errorLink");

        if (invite_link) {
          navigateTo(`${ROUTES.LANDING_PAGE}?link=${invite_link}`, {
            replace: true,
          });
          removeCookie("invite_link");
          return;
        }
        removeItem(TEMPORARY_ID_KEY);

        if (
          location.state &&
          location.state.buttonType &&
          location.state.buttonType === "buyGame"
        ) {
          // productType из лендинг-тарифа (REPEAT_TEST/EXTENDED_57/GAME);
          // undefined → PaymentPage подставит GAME по умолчанию
          navigateTo(ROUTES.PAYMENT_PAGE, {
            replace: true,
            state: { productType: location.state.productType },
          });
          return;
        }

        navigate(ROUTES.PERSONAL_ACCOUNT_PAGE, {
          replace: true,
        });
      },
      (error) => {
        errorMessage =
          error.response?.data?.message || t("email_code.register_error");
      }
    );
    return errorMessage;
  };

  const startLoadRegistration = () => {
    setIsRegistrationLoading(true);
  };

  const stopLoadRegistration = () => {
    setIsRegistrationLoading(false);
  };

  useEffect(() => {
    setRegisterFormData({
      ...registerFormData,
      birthday: formatDate(eighteenYearAgo),
    });
    detectGender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      setIsRegistrationLoading(null);
    };
  }, []);

  return {
    isRegistrationLoading,
    responseServerErr,
    onSubmitForm,
    comparableText,
    startLoadRegistration,
    stopLoadRegistration,
    registerFormData,
    onComparableTextHandle,
    handleChange,
    handleBlur,
    isValidTextInput,
    onCheckTerms,
    isTermsAcceptValid,
    isTermsAccept,
    onCheckPrivacy,
    isPrivacyAcceptValid,
    isPrivacyAccept,
    isRegisterFormValid,
    getValidGenders,
    onBirthdayHandle,
    isBirthdayValid,
    isValidEmail,
    comparableIsEmpty,
    isValidGenders,
    isValidPassword,
    isConsistency,
    handleComparableTextHandle,
  };
}
